import React, { useEffect, useState } from 'react';
import { Wrench, Loader2, Globe, Lock } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Big admin banner to toggle site-wide maintenance mode.
 * Always visible at the top of /admin, clearly showing current state.
 */
export default function MaintenanceToggle({ token }) {
  const [enabled, setEnabled] = useState(null);
  const [loading, setLoading] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/maintenance/status`);
        if (res.ok) setEnabled((await res.json()).enabled);
      } catch { /* ignore */ }
    })();
  }, [API_URL]);

  const toggle = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/maintenance/toggle`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('toggle failed');
      const data = await res.json();
      setEnabled(data.enabled);
      toast.success(data.enabled ? '🔒 Site basculé en maintenance' : '🌐 Site rouvert au public');
    } catch {
      toast.error('Impossible de changer le mode maintenance');
    } finally {
      setLoading(false);
    }
  };

  if (enabled === null) return null;

  const activeStyle = enabled
    ? 'bg-gradient-to-r from-red-950/60 to-red-900/30 border-red-500/40'
    : 'bg-gradient-to-r from-green-950/40 to-emerald-900/20 border-green-500/30';

  return (
    <div
      className={`rounded-xl border p-4 md:p-5 flex flex-col md:flex-row md:items-center justify-between gap-3 ${activeStyle}`}
      data-testid="admin-maintenance-banner"
    >
      <div className="flex items-center gap-3">
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0 ${
          enabled ? 'bg-red-500/20' : 'bg-green-500/20'
        }`}>
          {enabled
            ? <Lock className="w-5 h-5 text-red-400" />
            : <Globe className="w-5 h-5 text-green-400" />}
        </div>
        <div>
          <div className="flex items-center gap-2">
            <span className={`font-bold text-sm md:text-base ${enabled ? 'text-red-300' : 'text-green-300'}`}>
              {enabled ? 'Site en Maintenance' : 'Site public'}
            </span>
            <span className={`w-2 h-2 rounded-full ${enabled ? 'bg-red-400 animate-pulse' : 'bg-green-400'}`} />
          </div>
          <p className="text-xs text-gray-400 mt-0.5">
            {enabled
              ? 'Seuls les administrateurs peuvent accéder au site.'
              : 'Tout le monde peut accéder au site.'}
          </p>
        </div>
      </div>

      <button
        onClick={toggle}
        disabled={loading}
        className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:opacity-50 ${
          enabled
            ? 'bg-green-500 hover:bg-green-400 text-black'
            : 'bg-red-500 hover:bg-red-400 text-white'
        }`}
        data-testid="admin-maintenance-toggle"
      >
        {loading
          ? <Loader2 className="w-4 h-4 animate-spin" />
          : <Wrench className="w-4 h-4" />}
        {enabled ? 'Rouvrir le site' : 'Activer la maintenance'}
      </button>
    </div>
  );
}
